/**
 * Team Spain v0 — Vista "Hoy" (Agenda y Concentración) (M2)
 * 
 * Implementa la pantalla inicial de concentración y agenda diaria:
 * - Selector de fechas con navegación (día anterior, hoy, día siguiente) y chips de acceso rápido a la concentración demo.
 * - Tarjeta de concentración activa o próximo evento si el día consultado no tiene evento.
 * - Agenda cronológica con detección de actividad en curso y siguiente.
 * - Horarios en formato tabular, ubicación, responsables y notas compartidas.
 * - Notas internas de staff protegidas mediante RLS en backend.
 * - Enlaces directos al detalle de concentración (modal con tabs de agenda, sesiones y convocatoria).
 * - Estado vacío amigable que nunca deja la pantalla desierta.
 */

import React, { useState, useEffect, useMemo } from 'react'
import {
  CalendarDays,
  Clock,
  MapPin,
  Users,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Lock,
  Layers,
  AlertCircle,
  ClipboardList,
  HeartPulse,
  CheckCircle2,
} from 'lucide-react'
import { EventItem, CampActivity } from '../../types/calendar'
import { FormObligation, FormResponseRecord, HealthIncidentItem } from '../../types/health'
import { calendarService } from '../calendar/calendarService'
import { formsService } from '../health/formsService'
import { EventDetailModal, EventDetailTabKey } from '../calendar/EventDetailModal'
import { CampDailyCheckinModal } from '../health/CampDailyCheckinModal'
import { IncidentReportModal } from '../health/IncidentReportModal'
import { useAuth } from '../auth/AuthContext'
import { hasOperation } from '../auth/permissions'

interface CampsViewProps {
  onNavigateToCalendar?: (eventId?: string) => void
  onNavigateToHealth?: () => void
}

export const CampsView: React.FC<CampsViewProps> = ({
  onNavigateToCalendar,
  onNavigateToHealth,
}) => {
  const { user } = useAuth()
  const defaultTeamId =
    user?.memberships.find((m) => m.team_id)?.team_id || '33333333-3333-3333-3333-333333333333'

  const isStaff = user?.memberships.some((m) => m.is_active && (!m.team_id || m.team_id === defaultTeamId) && (hasOperation(m.roles, m.capabilities || [], 'manage_events') || m.roles.includes('medic')))

  // Fecha seleccionada
  // Si hoy no tiene actividades, los chips de demostración permiten saltar instantáneamente
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    // Si la URL tiene un parámetro ?date=YYYY-MM-DD
    const params = new URLSearchParams(window.location.search)
    const dateParam = params.get('date')
    if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam)) {
      const [y, m, d] = dateParam.split('-').map(Number)
      return new Date(y, m - 1, d)
    }
    // Por defecto hoy
    return new Date()
  })

  const [events, setEvents] = useState<EventItem[]>([])
  const [activities, setActivities] = useState<CampActivity[]>([])
  const [pendingObligations, setPendingObligations] = useState<FormObligation[]>([])
  const [staffPendingCount, setStaffPendingCount] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // Estados para Check-in Diario de Concentración (M8D)
  const [campCheckinModalOpen, setCampCheckinModalOpen] = useState(false)
  const [campIncidentModalOpen, setCampIncidentModalOpen] = useState(false)
  const [campDailyResponse, setCampDailyResponse] = useState<FormResponseRecord | null>(null)
  const [isAthleteConvened, setIsAthleteConvened] = useState(false)
  const [athleteIdForCheckin, setAthleteIdForCheckin] = useState<string | null>(null)
  const [openEpisodes, setOpenEpisodes] = useState<HealthIncidentItem[]>([])
  const [campDueTime, setCampDueTime] = useState<string>('09:00:00')
  const [checkinToast, setCheckinToast] = useState<string | null>(null)
  const [staffCampCompliance, setStaffCampCompliance] = useState<{
    total: number
    submitted: number
    late: number
    draft: number
    pending: number
    pendingContacts: number
  } | null>(null)

  // Modal de detalle de evento
  const [modalEvent, setModalEvent] = useState<{ id: string; tab?: EventDetailTabKey } | null>(null)

  const loadData = React.useCallback(async () => {
    setLoading(true)
    setErrorMsg(null)
    try {
      const [eventsData, activitiesData] = await Promise.all([
        calendarService.getEvents(defaultTeamId),
        calendarService.getAllCampActivities(defaultTeamId),
      ])
      setEvents(eventsData)
      setActivities(activitiesData)

      // Consultar formularios pendientes exclusivamente si el usuario tiene ficha deportiva de atleta vinculada
      if (user) {
        try {
          const { data: ath } = await (await import('../../lib/supabase')).supabase!
            .from('athletes')
            .select('id')
            .eq('user_id', user.id)
            .maybeSingle()

          const athId = ath?.id || (user.email === 'demo.player@teamspain.demo' ? 'a1111111-0000-0000-0000-000000000001' : null)
          if (athId) {
            setAthleteIdForCheckin(athId)
            const obls = await formsService.getAthletePendingObligations(defaultTeamId, athId)
            setPendingObligations(obls)
          } else {
            setAthleteIdForCheckin(null)
            setPendingObligations([])
          }

          if (isStaff) {
            try {
              const comp = await formsService.getTeamCompliance(defaultTeamId, '2026-10-12')
              const pending = comp.filter((c) => c.compliance_status === 'pending' || c.compliance_status === 'draft').length
              setStaffPendingCount(pending)
            } catch {
              // ignore
            }
          }
        } catch {
          // ignore
        }
      } else {
        setAthleteIdForCheckin(null)
        setPendingObligations([])
      }
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al cargar agenda y eventos.')
    } finally {
      setLoading(false)
    }
  }, [defaultTeamId, user, isStaff])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Formato YYYY-MM-DD local
  const formatYMD = (d: Date): string => {
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const selectedDateYMD = useMemo(() => formatYMD(selectedDate), [selectedDate])
  const todayYMD = useMemo(() => formatYMD(new Date()), [])

  // Evento activo en la fecha seleccionada (si la fecha cae entre start_time y end_time)
  const activeEventOnSelectedDate = useMemo(() => {
    const selStart = new Date(`${selectedDateYMD}T00:00:00Z`).getTime()
    const selEnd = new Date(`${selectedDateYMD}T23:59:59Z`).getTime()

    return events.find((ev) => {
      if (ev.status === 'cancelled') return false
      const evStart = new Date(ev.start_time).getTime()
      const evEnd = new Date(ev.end_time).getTime()
      return evStart <= selEnd && evEnd >= selStart
    })
  }, [events, selectedDateYMD])

  // Carga de estado de Check-in Diario de Concentración (M8D)
  const loadCampCheckin = React.useCallback(async () => {
    if (!activeEventOnSelectedDate) {
      setCampDailyResponse(null)
      setIsAthleteConvened(false)
      setStaffCampCompliance(null)
      return
    }

    const eventId = activeEventOnSelectedDate.id
    const dueTime = (activeEventOnSelectedDate as any).daily_checkin_due_time || '09:00:00'
    setCampDueTime(dueTime)

    try {
      const sb = (await import('../../lib/supabase')).supabase
      if (!sb) return

      let targetAthId: string | null = null
      if (user) {
        const { data: ath } = await sb
          .from('athletes')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle()
        const athId = ath?.id || (user.email === 'demo.player@teamspain.demo' ? 'a1111111-0000-0000-0000-000000000001' : null)
        if (athId) {
          targetAthId = athId
          setAthleteIdForCheckin(athId)
        } else {
          setAthleteIdForCheckin(null)
        }
      }

      // Si el usuario no tiene ficha deportiva propia de atleta, no tiene check-in personal
      if (!targetAthId) {
        setIsAthleteConvened(false)
        setCampDailyResponse(null)
      } else {
        // 1. Verificar convocatoria activa del atleta con rol 'athlete' (no staff/coach/medic/admin)
        const { data: part } = await sb
          .from('event_participants')
          .select('attendance_status, role')
          .eq('event_id', eventId)
          .eq('athlete_id', targetAthId)
          .eq('role', 'athlete')
          .maybeSingle()

        const isConvened = part && part.attendance_status !== 'declined' && part.attendance_status !== 'withdrawn'
        setIsAthleteConvened(Boolean(isConvened))

        if (isConvened) {
          const resp = await formsService.getCampDailyCheckin({
            teamId: defaultTeamId,
            athleteId: targetAthId,
            eventId,
            targetDate: selectedDateYMD,
          })
          setCampDailyResponse(resp)

          // Cargar incidencias abiertas para posible empeoramiento
          try {
            const incs = await formsService.getAthleteIncidents(targetAthId, defaultTeamId)
            setOpenEpisodes(incs.filter((i: HealthIncidentItem) => i.status === 'open' || i.status === 'monitoring'))
          } catch {
            // ignore
          }
        } else {
          setCampDailyResponse(null)
        }
      }

      // 2. Si es staff, cargar resumen de cumplimiento de la jornada
      if (isStaff) {
        try {
          const comp = await formsService.getCampDailyCompliance(defaultTeamId, eventId, selectedDateYMD)
          const total = comp.length
          const submitted = comp.filter((c) => c.compliance_status === 'submitted').length
          const late = comp.filter((c) => c.compliance_status === 'late_submitted' || c.is_late).length
          const draft = comp.filter((c) => c.compliance_status === 'draft').length
          const pending = comp.filter((c) => c.compliance_status === 'pending').length

          let pendingContacts = 0
          const isMedicRole = user?.memberships.some((m) =>
            m.roles.some((r) => ['medic', 'physio', 'doctor'].includes(r))
          )
          if (isMedicRole) {
            try {
              const medInbox = await formsService.getCampDailyMedicalInbox(defaultTeamId, eventId, selectedDateYMD)
              pendingContacts = medInbox.filter(
                (m) => m.contact_requested && m.contact_request_status === 'pending'
              ).length
            } catch {
              // ignore
            }
          }

          setStaffCampCompliance({
            total,
            submitted,
            late,
            draft,
            pending,
            pendingContacts,
          })
        } catch (err) {
          console.error('Error al cargar cumplimiento staff camp:', err)
        }
      }
    } catch (err) {
      console.error('Error al cargar check-in diario:', err)
    }
  }, [activeEventOnSelectedDate, selectedDateYMD, athleteIdForCheckin, user, isStaff, defaultTeamId])

  useEffect(() => {
    loadCampCheckin()
  }, [loadCampCheckin])

  // Próximo evento programado (para cuando no hay evento en la fecha consultada)
  const nextUpcomingEvent = useMemo(() => {
    const selTime = new Date(`${selectedDateYMD}T00:00:00Z`).getTime()
    const upcoming = events
      .filter((ev) => ev.status !== 'cancelled' && new Date(ev.end_time).getTime() >= selTime)
      .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())

    if (upcoming.length > 0) return upcoming[0]

    // Si no hay posterior a la fecha seleccionada, devolver el primer evento publicado disponible (ej. la concentración demo)
    return events.find((ev) => ev.status === 'published') || events[0] || null
  }, [events, selectedDateYMD])

  // Actividades de la fecha seleccionada
  const dayActivities = useMemo(() => {
    return activities.filter((act) => {
      // Comparar la fecha YYYY-MM-DD
      const actDate = act.start_time.slice(0, 10)
      return actDate === selectedDateYMD
    })
  }, [activities, selectedDateYMD])

  // Identificar actividad en curso y siguiente (cuando la fecha consultada es hoy)
  const { currentActivityId, nextActivityId } = useMemo(() => {
    if (selectedDateYMD !== todayYMD) {
      return { currentActivityId: null, nextActivityId: null }
    }
    const now = new Date().getTime()
    let current: string | null = null
    let next: string | null = null

    for (const act of dayActivities) {
      const start = new Date(act.start_time).getTime()
      const end = new Date(act.end_time).getTime()
      if (now >= start && now <= end && !current) {
        current = act.id
      } else if (now < start && !next) {
        next = act.id
      }
    }
    return { currentActivityId: current, nextActivityId: next }
  }, [dayActivities, selectedDateYMD, todayYMD])

  // Navegación de fechas
  const handlePrevDay = () => {
    const prev = new Date(selectedDate)
    prev.setDate(prev.getDate() - 1)
    setSelectedDate(prev)
  }

  const handleNextDay = () => {
    const next = new Date(selectedDate)
    next.setDate(next.getDate() + 1)
    setSelectedDate(next)
  }

  const handleSetDay = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-').map(Number)
    setSelectedDate(new Date(y, m - 1, d))
  }

  // Formato visual de la fecha
  const formattedDayTitle = useMemo(() => {
    return selectedDate.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }, [selectedDate])

  const formatTime = (isoString: string) => {
    const d = new Date(isoString)
    return d.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Madrid',
    })
  }

  return (
    <div>
      {/* Encabezado y Navegación de Fecha */}
      <div
        style={{
          marginBottom: '1.25rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '1rem',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.25rem' }}>
            <h1 style={{ fontSize: '1.4rem', fontWeight: 700, margin: 0 }}>Agenda y Concentración</h1>
            <span className="badge badge-muted" style={{ fontSize: '0.72rem' }}>
              Europe/Madrid
            </span>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.84rem', margin: 0 }}>
            Seguimiento de la jornada activa, actividades por unidad y sesiones de entrenamiento.
          </p>
        </div>

        {/* Acceso rápido a Calendario si se proporcionó callback */}
        {onNavigateToCalendar && (
          <button
            type="button"
            className="btn btn-secondary"
            style={{ fontSize: '0.82rem' }}
            onClick={() => onNavigateToCalendar()}
          >
            <CalendarDays size={15} style={{ marginRight: '0.4rem' }} />
            Ver Calendario Completo
          </button>
        )}
      </div>

      {/* Aviso de Formularios y Obligaciones Pendientes en Inicio (M6) */}
      {pendingObligations.length > 0 && (
        <div
          className="card"
          style={{
            marginBottom: '1.25rem',
            padding: '0.85rem 1rem',
            backgroundColor: 'var(--surface-primary)',
            borderLeft: pendingObligations.some((o) => o.status === 'overdue')
              ? '4px solid var(--brand-red)'
              : '4px solid var(--brand-yellow)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '0.75rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
            <div
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '50%',
                backgroundColor: pendingObligations.some((o) => o.status === 'overdue')
                  ? 'rgba(239, 68, 68, 0.12)'
                  : 'rgba(234, 179, 8, 0.12)',
                color: pendingObligations.some((o) => o.status === 'overdue')
                  ? 'var(--brand-red)'
                  : 'var(--brand-yellow)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <ClipboardList size={18} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                  {pendingObligations.length === 1
                    ? 'Tienes 1 formulario pendiente de entrega'
                    : `Tienes ${pendingObligations.length} formularios pendientes de entrega`}
                </span>
                <span
                  className={`badge ${
                    pendingObligations.some((o) => o.status === 'overdue')
                      ? 'badge-red'
                      : 'badge-warning'
                  }`}
                  style={{ fontSize: '0.7rem' }}
                >
                  {pendingObligations.some((o) => o.status === 'overdue')
                    ? 'Requiere atención'
                    : 'Pendiente'}
                </span>
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
                {pendingObligations.map((o) => o.title).join(' · ')}
              </div>
            </div>
          </div>

          {onNavigateToHealth && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={onNavigateToHealth}
              style={{ fontSize: '0.8rem', padding: '0.4rem 0.85rem' }}
            >
              Completar en Formularios
            </button>
          )}
        </div>
      )}

      {/* Aviso de Cumplimiento de Equipo para Staff en Inicio (M6) */}
      {isStaff && staffPendingCount !== null && staffPendingCount > 0 && (
        <div
          className="card"
          style={{
            marginBottom: '1.25rem',
            padding: '0.85rem 1rem',
            backgroundColor: 'var(--surface-primary)',
            borderLeft: '4px solid #3b82f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '0.75rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
            <div
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '50%',
                backgroundColor: 'rgba(59, 130, 246, 0.12)',
                color: '#3b82f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <ClipboardList size={18} />
            </div>
            <div>
              <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                Seguimiento Semanal del Equipo
              </span>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
                {staffPendingCount} {staffPendingCount === 1 ? 'jugador tiene' : 'jugadores tienen'} entregas pendientes o en borrador esta semana.
              </div>
            </div>
          </div>

          {onNavigateToHealth && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onNavigateToHealth}
              style={{ fontSize: '0.8rem', padding: '0.4rem 0.85rem' }}
            >
              Ver cumplimiento del equipo
            </button>
          )}
        </div>
      )}

      {/* Barra de Control de Fecha */}
      <div
        className="card"
        style={{
          marginBottom: '1.25rem',
          padding: '0.85rem 1rem',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.75rem',
          backgroundColor: 'var(--surface-primary)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handlePrevDay}
            style={{ padding: '0.4rem 0.6rem' }}
            title="Día anterior"
          >
            <ChevronLeft size={16} />
            <span style={{ fontSize: '0.78rem', marginLeft: '0.2rem' }}>Anterior</span>
          </button>

          <button
            type="button"
            className={`btn ${selectedDateYMD === todayYMD ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setSelectedDate(new Date())}
            style={{ fontSize: '0.78rem', padding: '0.4rem 0.75rem' }}
          >
            Hoy
          </button>

          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleNextDay}
            style={{ padding: '0.4rem 0.6rem' }}
            title="Día siguiente"
          >
            <span style={{ fontSize: '0.78rem', marginRight: '0.2rem' }}>Siguiente</span>
            <ChevronRight size={16} />
          </button>

          {/* Selector de fecha nativo */}
          <input
            type="date"
            className="form-input"
            value={selectedDateYMD}
            onChange={(e) => {
              if (e.target.value) handleSetDay(e.target.value)
            }}
            style={{
              padding: '0.35rem 0.6rem',
              fontSize: '0.82rem',
              width: 'auto',
              minWidth: '135px',
            }}
          />
        </div>

        {/* Chips de acceso directo a la concentración de demostración */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginRight: '0.2rem' }}>
            Concentración Demo:
          </span>
          <button
            type="button"
            onClick={() => handleSetDay('2026-05-15')}
            className={`btn ${selectedDateYMD === '2026-05-15' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ fontSize: '0.74rem', padding: '0.3rem 0.6rem' }}
          >
            Viernes 15 May
          </button>
          <button
            type="button"
            onClick={() => handleSetDay('2026-05-16')}
            className={`btn ${selectedDateYMD === '2026-05-16' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ fontSize: '0.74rem', padding: '0.3rem 0.6rem' }}
          >
            Sábado 16 May
          </button>
          <button
            type="button"
            onClick={() => handleSetDay('2026-05-17')}
            className={`btn ${selectedDateYMD === '2026-05-17' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ fontSize: '0.74rem', padding: '0.3rem 0.6rem' }}
          >
            Domingo 17 May
          </button>
        </div>
      </div>

      {errorMsg && (
        <div
          style={{
            padding: '0.85rem 1rem',
            backgroundColor: 'var(--status-danger-bg)',
            color: 'var(--status-danger-text)',
            borderRadius: 'var(--radius-md)',
            marginBottom: '1rem',
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <AlertCircle size={16} />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* TARJETA CHECK-IN DE HOY (M8D — Atleta convocado en concentración) */}
      {/* ------------------------------------------------------------- */}
      {activeEventOnSelectedDate && isAthleteConvened && athleteIdForCheckin && (
        <div
          className="card"
          style={{
            marginBottom: '1.25rem',
            padding: '1.1rem 1.25rem',
            backgroundColor: 'var(--surface-primary)',
            borderLeft:
              campDailyResponse?.status === 'submitted'
                ? '4px solid #2563eb'
                : '4px solid #d97706',
            border: '1px solid var(--surface-border)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.85rem' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '10px',
                backgroundColor:
                  campDailyResponse?.status === 'submitted'
                    ? 'rgba(37, 99, 235, 0.12)'
                    : 'rgba(217, 119, 6, 0.12)',
                color:
                  campDailyResponse?.status === 'submitted'
                    ? '#2563eb'
                    : '#d97706',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <HeartPulse size={22} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.2rem' }}>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
                  Check-in de hoy
                </h3>
                {campDailyResponse?.status === 'submitted' ? (
                  <span className="badge badge-success" style={{ fontSize: '0.72rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                    <CheckCircle2 size={12} />
                    {campDailyResponse.is_late ? 'Enviado (Tardío)' : 'Enviado a tiempo'} · Rev. #{campDailyResponse.revision || 1}
                  </span>
                ) : campDailyResponse?.status === 'draft' ? (
                  <span className="badge badge-info" style={{ fontSize: '0.72rem' }}>
                    Borrador guardado online
                  </span>
                ) : (
                  <span className="badge badge-warning" style={{ fontSize: '0.72rem' }}>
                    Pendiente de entrega
                  </span>
                )}
              </div>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: 0 }}>
                <strong>{activeEventOnSelectedDate.title}</strong> · {formattedDayTitle}
                <span style={{ marginLeft: '0.5rem', color: 'var(--text-muted)' }}>
                  (Límite diario: {campDueTime.slice(0, 5)} h · Europe/Madrid)
                </span>
              </p>
              {checkinToast && (
                <p style={{ fontSize: '0.78rem', color: '#16a34a', margin: '0.3rem 0 0', fontWeight: 600 }}>
                  ✓ {checkinToast}
                </p>
              )}
            </div>
          </div>

          <div>
            {campDailyResponse?.status === 'submitted' ? (
              <button
                type="button"
                onClick={() => setCampCheckinModalOpen(true)}
                className="btn btn-secondary"
                style={{ fontSize: '0.84rem', padding: '0.55rem 1rem', minHeight: '44px' }}
              >
                Consultar o corregir respuesta
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setCampCheckinModalOpen(true)}
                className="btn btn-primary"
                style={{ fontSize: '0.84rem', padding: '0.55rem 1.15rem', minHeight: '44px', fontWeight: 600 }}
              >
                {campDailyResponse?.status === 'draft' ? 'Continuar y enviar check-in' : 'Completar check-in'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* SEGUIMIENTO DEL CHECK-IN DIARIO PARA STAFF (M8D) */}
      {/* ------------------------------------------------------------- */}
      {isStaff && activeEventOnSelectedDate && staffCampCompliance && (
        <div
          className="card"
          style={{
            marginBottom: '1.25rem',
            padding: '1rem 1.25rem',
            backgroundColor: 'var(--surface-primary)',
            borderLeft: '4px solid #3b82f6',
            border: '1px solid var(--surface-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '8px',
                backgroundColor: 'rgba(59, 130, 246, 0.12)',
                color: '#3b82f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <HeartPulse size={20} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <h4 style={{ fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>
                  Seguimiento del Check-in Diario
                </h4>
                <span className="badge badge-muted" style={{ fontSize: '0.72rem' }}>
                  {formattedDayTitle}
                </span>
                {staffCampCompliance.pendingContacts > 0 && (
                  <span className="badge badge-red" style={{ fontSize: '0.72rem' }}>
                    {staffCampCompliance.pendingContacts} {staffCampCompliance.pendingContacts === 1 ? 'solicitud médica prioritaria' : 'solicitudes médicas prioritarias'}
                  </span>
                )}
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0.2rem 0 0' }}>
                <strong>{staffCampCompliance.submitted + staffCampCompliance.late} / {staffCampCompliance.total}</strong> entregados ({Math.round(((staffCampCompliance.submitted + staffCampCompliance.late) / (staffCampCompliance.total || 1)) * 100)}%)
                · {staffCampCompliance.pending} pendientes · {staffCampCompliance.late} tardíos · {staffCampCompliance.draft} borradores
              </p>
            </div>
          </div>

          {onNavigateToHealth && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onNavigateToHealth}
              style={{ fontSize: '0.8rem', padding: '0.45rem 0.9rem', minHeight: '44px' }}
            >
              Consultar en Formularios
            </button>
          )}
        </div>
      )}

      {/* Tarjeta Destacada de Formularios Pendientes en Inicio (M6 Requerimiento 4) */}
      {pendingObligations.length > 0 && (
        <div
          className="card"
          style={{
            marginBottom: '1.25rem',
            padding: '1rem 1.25rem',
            backgroundColor: 'var(--surface-sunken)',
            borderLeft: pendingObligations.some((o) => o.status === 'overdue')
              ? '4px solid var(--brand-red)'
              : '4px solid var(--brand-yellow)',
            border: '1px solid var(--surface-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.75rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '8px',
                backgroundColor: pendingObligations.some((o) => o.status === 'overdue')
                  ? 'rgba(239, 68, 68, 0.15)'
                  : 'rgba(234, 179, 8, 0.15)',
                color: pendingObligations.some((o) => o.status === 'overdue')
                  ? 'var(--brand-red)'
                  : 'var(--brand-yellow)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <ClipboardList size={20} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <h4 style={{ fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>
                  Tienes formularios de seguimiento pendientes
                </h4>
                <span
                  className={`badge ${
                    pendingObligations.some((o) => o.status === 'overdue')
                      ? 'badge-red'
                      : 'badge-warning'
                  }`}
                >
                  {pendingObligations.length} {pendingObligations.length === 1 ? 'pendiente' : 'pendientes'}
                </span>
              </div>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '0.2rem 0 0' }}>
                {pendingObligations[0]?.title}
                {pendingObligations[0]?.period_start ? ` (${pendingObligations[0].period_start} al ${pendingObligations[0].period_end})` : ''}
                {pendingObligations[0]?.status === 'draft' ? ' · Borrador guardado online' : ''}
              </p>
            </div>
          </div>

          <div>
            <button
              type="button"
              onClick={() => onNavigateToHealth?.()}
              className="btn btn-primary"
              style={{ fontSize: '0.78rem', padding: '0.4rem 0.85rem' }}
            >
              {pendingObligations[0]?.status === 'draft' ? 'Continuar borrador' : 'Completar ahora'}
            </button>
          </div>
        </div>
      )}

      {/* Tarjeta de Concentración / Evento Activo en la Fecha */}
      {activeEventOnSelectedDate ? (
        <div
          className="card"
          style={{
            marginBottom: '1.25rem',
            borderLeft: '4px solid var(--brand-red)',
            padding: '1.25rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: '0.75rem',
            }}
          >
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                <span className="badge badge-red">Concentración Nacional</span>
                {activeEventOnSelectedDate.status === 'draft' && (
                  <span className="badge badge-warning">Borrador Staff</span>
                )}
              </div>
              <h2 style={{ fontSize: '1.2rem', fontWeight: 700, margin: '0 0 0.4rem 0' }}>
                {activeEventOnSelectedDate.title}
              </h2>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1.25rem',
                  fontSize: '0.82rem',
                  color: 'var(--text-secondary)',
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <CalendarDays size={14} style={{ color: 'var(--brand-red)' }} />
                  {new Date(activeEventOnSelectedDate.start_time).toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'short',
                  })}{' '}
                  al{' '}
                  {new Date(activeEventOnSelectedDate.end_time).toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <MapPin size={14} style={{ color: 'var(--text-muted)' }} />
                  {activeEventOnSelectedDate.location || 'Sede deportiva'}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Clock size={14} style={{ color: 'var(--text-muted)' }} />
                  {dayActivities.length} actividades programadas hoy
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                type="button"
                className="btn btn-primary"
                style={{ fontSize: '0.82rem' }}
                onClick={() => setModalEvent({ id: activeEventOnSelectedDate.id, tab: 'agenda' })}
              >
                Ver Concentración Completa
              </button>
            </div>
          </div>
        </div>
      ) : nextUpcomingEvent ? (
        /* Tarjeta informativa del próximo evento si la fecha consultada no tiene evento propio */
        <div
          className="card"
          style={{
            marginBottom: '1.25rem',
            padding: '1rem 1.25rem',
            backgroundColor: 'var(--surface-primary)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '0.75rem',
            }}
          >
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.2rem' }}>
                <Sparkles size={14} style={{ color: 'var(--brand-red)' }} />
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                  PRÓXIMA CONCENTRACIÓN DISPONIBLE
                </span>
              </div>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 600, margin: '0 0 0.25rem 0' }}>
                {nextUpcomingEvent.title}
              </h3>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                {new Date(nextUpcomingEvent.start_time).toLocaleDateString('es-ES', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}{' '}
                · {nextUpcomingEvent.location}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ fontSize: '0.78rem' }}
                onClick={() => handleSetDay(nextUpcomingEvent.start_time.slice(0, 10))}
              >
                Consultar esa fecha ({nextUpcomingEvent.start_time.slice(8, 10)}{' '}
                {new Date(nextUpcomingEvent.start_time).toLocaleDateString('es-ES', { month: 'short' })})
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ fontSize: '0.78rem' }}
                onClick={() => setModalEvent({ id: nextUpcomingEvent.id, tab: 'details' })}
              >
                Abrir Detalle
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Sección de la Agenda del Día */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div
          style={{
            padding: '1rem 1.25rem',
            borderBottom: '1px solid var(--surface-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.5rem',
            backgroundColor: 'var(--surface-primary)',
          }}
        >
          <div>
            <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 600 }}>
              Cronograma de la jornada
            </span>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, margin: '0.1rem 0 0 0', textTransform: 'capitalize' }}>
              {formattedDayTitle}
            </h3>
          </div>
          <span className="badge badge-muted tabular-numbers">
            {dayActivities.length} {dayActivities.length === 1 ? 'actividad' : 'actividades'}
          </span>
        </div>

        {loading ? (
          <div style={{ padding: '3rem 1.5rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: '0.9rem' }}>Cargando agenda de la jornada...</div>
          </div>
        ) : dayActivities.length === 0 ? (
          /* Estado vacío amigable cuando el día consultado no tiene actividades */
          <div style={{ padding: '3rem 1.5rem', textAlign: 'center' }}>
            <CalendarDays size={38} style={{ color: 'var(--text-muted)', marginBottom: '0.75rem' }} />
            <h4 style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '0.35rem' }}>
              Sin actividades programadas para este día
            </h4>
            <p
              style={{
                fontSize: '0.85rem',
                color: 'var(--text-secondary)',
                maxWidth: '440px',
                margin: '0 auto 1.5rem auto',
                lineHeight: 1.5,
              }}
            >
              No hay entrenamientos, reuniones ni actividades registradas para el {formattedDayTitle}.
              Puedes consultar los días de la concentración de demostración (15 al 17 de mayo de 2026).
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => handleSetDay('2026-05-15')}
                style={{ fontSize: '0.84rem' }}
              >
                <Sparkles size={15} style={{ marginRight: '0.4rem' }} />
                Ver Concentración Demo (15 Mayo)
              </button>
              {onNavigateToCalendar && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => onNavigateToCalendar()}
                  style={{ fontSize: '0.84rem' }}
                >
                  <CalendarDays size={15} style={{ marginRight: '0.4rem' }} />
                  Ver Calendario Mensual
                </button>
              )}
            </div>
          </div>
        ) : (
          /* Lista cronológica estructurada de actividades */
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {dayActivities.map((act, idx) => {
              const isCurrent = act.id === currentActivityId
              const isNext = act.id === nextActivityId

              const unitBadge =
                act.target_unit === 'offense' ? (
                  <span className="badge badge-unit-offense">Ataque</span>
                ) : act.target_unit === 'defense' ? (
                  <span className="badge badge-unit-defense">Defensa</span>
                ) : act.target_unit === 'staff' ? (
                  <span className="badge badge-unit-staff">Staff</span>
                ) : (
                  <span className="badge badge-muted">Todo el equipo</span>
                )

              const typeBadge =
                act.activity_type === 'practice' ? (
                  <span className="badge badge-red">Entrenamiento</span>
                ) : act.activity_type === 'meeting' ? (
                  <span className="badge badge-info">Meeting</span>
                ) : act.activity_type === 'meal' ? (
                  <span className="badge badge-warning">Comida</span>
                ) : act.activity_type === 'rest' ? (
                  <span className="badge badge-muted">Descanso</span>
                ) : act.activity_type === 'transit' ? (
                  <span className="badge badge-muted">Traslado</span>
                ) : (
                  <span className="badge badge-muted">Actividad</span>
                )

              return (
                <div
                  key={act.id}
                  style={{
                    padding: '1rem 1.25rem',
                    borderBottom: idx < dayActivities.length - 1 ? '1px solid var(--surface-border)' : 'none',
                    backgroundColor: isCurrent
                      ? 'rgba(197, 18, 48, 0.04)'
                      : isNext
                        ? 'rgba(13, 27, 42, 0.03)'
                        : 'transparent',
                    borderLeft: isCurrent
                      ? '4px solid var(--brand-red)'
                      : isNext
                        ? '4px solid var(--brand-navy)'
                        : '4px solid transparent',
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      flexWrap: 'wrap',
                      gap: '0.75rem',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: '260px' }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          flexWrap: 'wrap',
                          marginBottom: '0.35rem',
                        }}
                      >
                        {/* Horario en cifras tabulares */}
                        <span
                          className="tabular-numbers"
                          style={{
                            fontWeight: 700,
                            fontSize: '0.92rem',
                            color: 'var(--text-main)',
                            backgroundColor: 'var(--surface-primary)',
                            padding: '0.15rem 0.5rem',
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--border-subtle)',
                          }}
                        >
                          {formatTime(act.start_time)} – {formatTime(act.end_time)}
                        </span>

                        {typeBadge}
                        {unitBadge}

                        {isCurrent && (
                          <span
                            className="badge badge-success"
                            style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                          >
                            <span
                              style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                backgroundColor: 'currentColor',
                              }}
                            />
                            Actividad en curso
                          </span>
                        )}

                        {isNext && (
                          <span className="badge badge-info" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            A continuación
                          </span>
                        )}
                      </div>

                      <h4 style={{ fontSize: '1rem', fontWeight: 600, margin: '0.2rem 0 0.35rem 0' }}>
                        {act.title}
                      </h4>

                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1.25rem',
                          fontSize: '0.8rem',
                          color: 'var(--text-secondary)',
                          flexWrap: 'wrap',
                        }}
                      >
                        {act.location && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                            <MapPin size={13} style={{ color: 'var(--text-muted)' }} />
                            {act.location}
                          </span>
                        )}
                        {act.responsibles && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                            <Users size={13} style={{ color: 'var(--text-muted)' }} />
                            {act.responsibles}
                          </span>
                        )}
                      </div>

                      {/* Notas compartidas con los atletas */}
                      {act.shared_notes && (
                        <div
                          style={{
                            marginTop: '0.5rem',
                            fontSize: '0.82rem',
                            color: 'var(--text-secondary)',
                            backgroundColor: 'var(--surface-primary)',
                            padding: '0.4rem 0.75rem',
                            borderRadius: 'var(--radius-sm)',
                            borderLeft: '2px solid var(--border-subtle)',
                          }}
                        >
                          {act.shared_notes}
                        </div>
                      )}

                      {/* Notas internas de staff protegidas físicamente */}
                      {isStaff && act.internal_notes && (
                        <div
                          style={{
                            marginTop: '0.5rem',
                            fontSize: '0.8rem',
                            color: 'var(--text-main)',
                            backgroundColor: 'rgba(217, 119, 6, 0.08)',
                            padding: '0.45rem 0.75rem',
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid rgba(217, 119, 6, 0.25)',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.4rem',
                          }}
                        >
                          <Lock size={13} style={{ color: '#d97706', marginTop: '0.15rem', flexShrink: 0 }} />
                          <div>
                            <strong style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#b45309' }}>
                              Nota interna de staff:
                            </strong>{' '}
                            <span>{act.internal_notes}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Acciones contextuales */}
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', alignSelf: 'flex-start' }}>
                      {act.activity_type === 'practice' && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ fontSize: '0.75rem', padding: '0.35rem 0.65rem' }}
                          onClick={() => setModalEvent({ id: act.event_id, tab: 'sessions' })}
                        >
                          <Layers size={13} style={{ marginRight: '0.3rem' }} />
                          Plan de sesión
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ fontSize: '0.75rem', padding: '0.35rem 0.65rem' }}
                        onClick={() => setModalEvent({ id: act.event_id, tab: 'agenda' })}
                      >
                        Ver concentración
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Modal de Detalle de Evento */}
      {modalEvent && (
        <EventDetailModal
          eventId={modalEvent.id}
          initialTab={modalEvent.tab}
          onClose={() => setModalEvent(null)}
          onEventUpdated={loadData}
        />
      )}

      {/* Modal de Check-in Diario de Concentración (M8D) */}
      {campCheckinModalOpen && activeEventOnSelectedDate && athleteIdForCheckin && (
        <CampDailyCheckinModal
          isOpen={campCheckinModalOpen}
          onClose={() => setCampCheckinModalOpen(false)}
          onSuccess={(status) => {
            setCheckinToast(
              status === 'draft'
                ? 'Borrador online guardado correctamente.'
                : 'Check-in diario enviado con éxito.'
            )
            loadCampCheckin()
            loadData()
            setTimeout(() => setCheckinToast(null), 4000)
          }}
          teamId={defaultTeamId}
          athleteId={athleteIdForCheckin}
          eventId={activeEventOnSelectedDate.id}
          eventTitle={activeEventOnSelectedDate.title}
          targetDate={selectedDateYMD}
          dueTime={campDueTime}
          initialResponse={campDailyResponse}
          openEpisodes={openEpisodes}
          onOpenIncidentModal={() => setCampIncidentModalOpen(true)}
        />
      )}

      {/* Modal de Molestias / Incidencias bajo demanda (M8D integración) */}
      {campIncidentModalOpen && athleteIdForCheckin && (
        <IncidentReportModal
          isOpen={campIncidentModalOpen}
          onClose={() => setCampIncidentModalOpen(false)}
          onSuccess={() => {
            loadCampCheckin()
          }}
          teamId={defaultTeamId}
          athleteId={athleteIdForCheckin}
          openEpisodes={openEpisodes}
        />
      )}
    </div>
  )
}
